/***
 *
 *   VIEW
 *   The view houses global components that are common to all views
 *   (notification, modal), handles errors and renders the layout
 *
 **********/

import React, { Suspense, useState } from "react";
import {
  AppLayout,
  AuthLayout,
  HomeLayout,
  OnboardingLayout,
  Modal,
  Notification,
  Loader,
  useNavigate,
} from "../lib";
import Swal from "sweetalert2";

// import './scss/normalize.scss';
// import './scss/view.scss';
// import './scss/typography.scss';

export const ViewContext = React.createContext();

export function View(props) {
  const navigate = useNavigate();

  // state
  const [notification, setNotification] = useState({
    visible: "hide",
    autoclose: true,
  });
  const [modal, setModal] = useState({});
  const [loading, setLoading] = useState(false);

  // layouts
  const layouts = {
    app: AppLayout,
    home: HomeLayout,
    auth: AuthLayout,
    onboarding: OnboardingLayout,
  };

  // set title & layout
  document.title = props.title;
  let Layout = props.layout ? layouts[props.layout] : AppLayout;

  if (!props.display) return false;

  function showNotification(text, type, autoclose, format, icon) {
    setNotification({
      text: text,
      type: type,
      show: true,
      format: format,
      icon: icon,
      autoclose: autoclose,
    });

    if (autoclose) setTimeout(hideNotification, 2000);
  }

  function hideNotification() {
    setNotification({
      text: "",
      type: "",
      show: false,
      format: null,
      icon: null,
      autoclose: true,
    });
  }

  function showModal(content, callback) {
    let data = { ...modal };

    if (content) {
      for (let key in content) data[key] = content[key];

      data.show = true;
      data.callback = callback;
    }

    setModal(data);
  }

  function hideModal(cancel, res) {
    // callback?
    if (!cancel && modal.callback) modal.callback(modal.form, res);

    // reset the modal
    setModal({
      title: null,
      text: null,
      buttonText: null,
      buttonTextCancel: null,
      url: null,
      method: null,
      form: null,
      body: null,         
      closeOnOutsideClick: true,
      numberGrid: null,
      destructive: false,
      show: false,
      callback: null,
    });
  }

  // Funcion auxiliar para parsear errores
  function parseErrorMessage(err) {
    let message = "";

    if (typeof err === "string") {
      message = err;
    } else if (err?.response?.data?.message) {
      message = err.response.data.message;
    } else if (err?.message) {
      message = err.message;
    } else if (err?.toString) {
      message = err.toString();
    }

    // Verificar si es error de duplicado
    if (message.toLowerCase().includes("duplicate entry")) {
      const match = message.match(/Duplicate entry '([^']+)' for key '([^']+)'/i);
      if (match) {
        const value = match[1];
        const keyName = match[2].toLowerCase();

        let fieldLabel = "registro";
        if (keyName.includes("identification")) fieldLabel = "cédula";
        else if (keyName.includes("email")) fieldLabel = "correo electrónico";
        else if (keyName.includes("plate")) fieldLabel = "placa";
        else if (keyName.includes("phone")) fieldLabel = "teléfono";

        return {
          title: "Registro duplicado",
          html: `Ya existe una ${fieldLabel} con el valor <span style="color: ${colors.error}; font-weight: 600;">${value}</span>. Por favor, utilice un valor diferente.`,
          type: "duplicate",
        };

      }
      return {
        title: "Registro duplicado",
        message: "El registro que intenta crear ya existe en el sistema.",
        type: "duplicate",
      };
    }

    // Si el mensaje es muy largo (SQL), dar mensaje generico
    if (message.length > 200) {
      return {
        title: "Error",
        message: "Ocurrió un error al procesar la solicitud. Contacte al administrador.",
        type: "unknown",
      };
    }

    return {
      title: "Error",
      message: message || "Ocurrió un error inesperado.",
      type: "unknown",
    };
  }

  // Colores del proyecto
  const colors = {
    success: '#10b981',
    error: '#d95565',
    warning: '#F0AA61',
    info: '#73B0F4',
    purple: '#6363AC',
  };

  // Colores tenues para fondos
  const bgColors = {
    success: 'rgba(16, 185, 129, 0.1)',
    error: 'rgba(217, 85, 101, 0.1)',
    warning: 'rgba(240, 170, 97, 0.1)',
    info: 'rgba(115, 176, 244, 0.1)',
    purple: 'rgba(99, 99, 172, 0.1)',
  };

  // CSS para animaciones (inyectar una vez)
  if (typeof document !== 'undefined' && !document.getElementById('swal-view-animations')) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'swal-view-animations';
    styleSheet.textContent = `
      @keyframes scaleIn {
        0% { transform: scale(0.5); opacity: 0; }
        60% { transform: scale(1.05); }
        100% { transform: scale(1); opacity: 1; }
      }
      @keyframes drawLine {
        0% { stroke-dashoffset: 50; }
        100% { stroke-dashoffset: 0; }
      }
      .swal-icon-animated {
        animation: scaleIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
      }
      .swal-icon-animated .icon-circle {
        animation: scaleIn 0.4s ease-out forwards;
      }
      .swal-icon-animated .icon-check {
        stroke-dasharray: 50;
        stroke-dashoffset: 50;
        animation: drawLine 0.5s ease-out 0.3s forwards;
      }
      .swal-icon-animated .icon-x {
        stroke-dasharray: 50;
        stroke-dashoffset: 50;
        animation: drawLine 0.4s ease-out 0.3s forwards;
      }
      /* Estilos del modal */
      .swal2-popup.swal2-modal {
        border-radius: 16px !important;
        padding: 1.25rem 1.5rem 1.5rem !important;
        box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.12) !important;
      }
      .swal2-title {
        font-size: 1.2rem !important;
        font-weight: 600 !important;
        color: #1f2937 !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      .swal2-html-container {
        font-size: 0.9rem !important;
        color: #6b7280 !important;
        line-height: 1.5 !important;
        margin: 0.5rem 0 0.75rem !important;
        padding: 0 !important;
      }
      .swal2-icon {
        border: none !important;
        margin: 0.75rem auto 0.5rem !important;
        width: auto !important;
        height: auto !important;
      }
      .swal2-actions {
        margin: 0.5rem 0 0 !important;
        padding: 0 !important;
      }
    `;
    document.head.appendChild(styleSheet);
  }

  // Iconos SVG elegantes y finos
  const swalIcons = {
    success: `
      <div class="swal-icon-animated">
        <svg viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg" style="width: 100px; height: 100px;">
          <circle class="icon-circle" cx="25" cy="25" r="23" fill="${bgColors.success}" stroke="${colors.success}" stroke-width="1.5"/>
          <path class="icon-check" d="M15 26l7 7 13-14" stroke="${colors.success}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
        </svg>
      </div>
    `,
    error: `
      <div class="swal-icon-animated">
        <svg viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg" style="width: 100px; height: 100px;">
          <circle class="icon-circle" cx="25" cy="25" r="23" fill="${bgColors.error}" stroke="${colors.error}" stroke-width="1.5"/>
          <path class="icon-x" d="M17 17l16 16" stroke="${colors.error}" stroke-width="2" stroke-linecap="round"/>
          <path class="icon-x" d="M33 17l-16 16" stroke="${colors.error}" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </div>
    `,
    warning: `
      <div class="swal-icon-animated">
        <svg viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg" style="width: 100px; height: 100px;">
          <path class="icon-circle" d="M25 4L3 46h44L25 4z" fill="${bgColors.warning}" stroke="${colors.warning}" stroke-width="1.5" stroke-linejoin="round"/>
          <path d="M25 18v12" stroke="${colors.warning}" stroke-width="2" stroke-linecap="round"/>
          <circle cx="25" cy="36" r="1.5" fill="${colors.warning}"/>
        </svg>
      </div>
    `,
    info: `
      <div class="swal-icon-animated">
        <svg viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg" style="width: 100px; height: 100px;">
          <circle class="icon-circle" cx="25" cy="25" r="23" fill="${bgColors.info}" stroke="${colors.info}" stroke-width="1.5"/>
          <path d="M25 22v12" stroke="${colors.info}" stroke-width="2" stroke-linecap="round"/>
          <circle cx="25" cy="15" r="1.5" fill="${colors.info}"/>
        </svg>
      </div>
    `,
    question: `
      <div class="swal-icon-animated">
        <svg viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg" style="width: 100px; height: 100px;">
          <circle class="icon-circle" cx="25" cy="25" r="23" fill="${bgColors.purple}" stroke="${colors.purple}" stroke-width="1.5"/>
          <path class="icon-check" d="M19 18a6 6 0 0111 3c0 4-6 5-6 9" stroke="${colors.purple}" stroke-width="2" stroke-linecap="round" fill="none"/>
          <circle cx="25" cy="36" r="1.5" fill="${colors.purple}"/>
        </svg>
      </div>
    `,
  };

  // Configuración de botones
  const buttonConfigs = {
    success: { bg: colors.success, hover: '#0d9668' },
    error: { bg: colors.error, hover: '#c44455' },
    warning: { bg: colors.warning, hover: '#d99550' },
    info: { bg: colors.info, hover: '#5a9de0' },
    cancel: { bg: '#e5e7eb', hover: '#d1d5db', textColor: '#374151' },
  };

  // Función para crear estilo de botón
  const createBtnStyle = (bg) => `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 10px 24px;
    font-size: 14px;
    font-weight: 600;
    color: white;
    background-color: ${bg};
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  `;

  // Función helper para aplicar estilos
  function applyBtnStyles(popup, type, hasCancel = false) {
    const confirmBtn = popup.querySelector('.swal2-confirm');
    const cancelBtn = popup.querySelector('.swal2-cancel');
    const config = buttonConfigs[type];

    if (confirmBtn && config) {
      confirmBtn.style.cssText = createBtnStyle(config.bg);
      confirmBtn.onmouseenter = () => { confirmBtn.style.backgroundColor = config.hover; };
      confirmBtn.onmouseleave = () => { confirmBtn.style.backgroundColor = config.bg; };
    }

    if (cancelBtn && hasCancel) {
      const cancelConfig = buttonConfigs.cancel;
      cancelBtn.style.cssText = createBtnStyle(cancelConfig.bg);
      cancelBtn.style.color = cancelConfig.textColor;
      cancelBtn.style.marginLeft = '12px';
      cancelBtn.onmouseenter = () => { cancelBtn.style.backgroundColor = cancelConfig.hover; };
      cancelBtn.onmouseleave = () => { cancelBtn.style.backgroundColor = cancelConfig.bg; };
    }
  }

  // Mostrar alerta de ERROR con SweetAlert2
  function showSwalError(title, message, isHtml = false) {
    const config = {
      customClass: { icon: "border-0" },
      buttonsStyling: false,
      title: title,
      iconHtml: swalIcons.error,
      confirmButtonText: "Cerrar",
      allowOutsideClick: false,
      allowEscapeKey: true,
      didOpen: (popup) => applyBtnStyles(popup, 'error'),
    };

    if (isHtml) {
      config.html = message;
    } else {
      config.text = message;
    }

    return Swal.fire(config);
  }

  // Mostrar alerta de EXITO con SweetAlert2
  function showSwalSuccess(title, message) {
    return Swal.fire({
      customClass: { icon: "border-0" },
      buttonsStyling: false,
      title: title,
      text: message,
      iconHtml: swalIcons.success,
      confirmButtonText: "Aceptar",
      timer: 3000,
      timerProgressBar: true,
      didOpen: (popup) => applyBtnStyles(popup, 'success'),
    });
  }

  // Mostrar alerta de ADVERTENCIA con SweetAlert2
  function showSwalWarning(title, message) {
    return Swal.fire({
      customClass: { icon: "border-0" },
      buttonsStyling: false,
      title: title,
      text: message,
      iconHtml: swalIcons.warning,
      confirmButtonText: "Entendido",
      didOpen: (popup) => applyBtnStyles(popup, 'warning'),
    });
  }

  // Mostrar alerta de INFORMACION con SweetAlert2
  function showSwalInfo(title, message) {
    return Swal.fire({
      customClass: { icon: "border-0" },
      buttonsStyling: false,
      title: title,
      text: message,
      iconHtml: swalIcons.info,
      confirmButtonText: "Entendido",
      didOpen: (popup) => applyBtnStyles(popup, 'info'),
    });
  }

  // Mostrar CONFIRMACION con SweetAlert2
  async function showSwalConfirm(title, message, confirmText = "Si, continuar", cancelText = "Cancelar") {
    const result = await Swal.fire({
      customClass: { icon: "border-0" },
      buttonsStyling: false,
      title: title,
      text: message,
      iconHtml: swalIcons.question,
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
      reverseButtons: true,
      didOpen: (popup) => applyBtnStyles(popup, 'error', true),
    });
    return result.isConfirmed;
  }

  function handleError(err) {
    if (err) {
      // Verificar si hay respuesta del servidor
      if (err.response) {
        const status = err.response.status;

        // Manejar casos especiales de status
        switch (status) {
          case 401:
            navigate("/");
            return;

          case 404:
            navigate("/notfound");
            return;

          case 402: // payment required
            navigate("/account/upgrade?plan=" + err.response.data.plan);
            return;

          case 429:
            showNotification(err.response.data, "error", false);
            return;

          default:
            // Usar el parser de errores para mostrar mensajes amigables
            const parsed = parseErrorMessage(err);
            console.error("Error original:", err);

            // Mostrar alerta con SweetAlert2 para errores de duplicado
            if (parsed.type === "duplicate") {
              if (parsed.html) {
                showSwalError(parsed.title, parsed.html, true);
              } else {
                showSwalError(parsed.title, parsed.message);
              }
            } else {
              // Para otros errores, usar notificacion
              showNotification(parsed.message, "error", false);
            }
            break;
        }
      } else {
        // Error sin respuesta del servidor
        const parsed = parseErrorMessage(err);
        showNotification(parsed.message, "error", false);
      }
    } else {
      showNotification("Se produjo un error inesperado. Intentelo de nuevo.", "error", false);
    }
  }

  const context = {
    notification: {
      show: showNotification,
      hide: hideNotification,
      data: notification,
    },
    modal: {
      show: showModal,
      hide: hideModal,
      data: modal,
    },
    // Alertas con SweetAlert2
    alert: {
      error: showSwalError,
      success: showSwalSuccess,
      warning: showSwalWarning,
      info: showSwalInfo,
      confirm: showSwalConfirm,
    },

    setLoading: (state) => setLoading(state),
    handleError: handleError,
  };

  return (
    <ViewContext.Provider value={context}>
      {notification.show && <Notification {...notification} />}

      {loading && <Loader fullScreen />}

      {modal.show && <Modal {...modal} />}

      <Suspense fallback={<Loader />}>
        <Layout title={props.title} data={props.data}>
          {props.display}
        </Layout>
      </Suspense>
    </ViewContext.Provider>
  );
}
